import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
  Chip,
  Tooltip,
  Avatar,
  Slider,
  InputAdornment,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Videocam as TikTokIcon,
  RocketLaunch as RocketIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  DeleteSweep as ClearIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import {
  searchCreators, getQuota, findEmail, createPendingAccount, getBlogVideoStatus,
  getOffersForAutomation, getCategoriesForAutomation,
  downloadVideoLocally, attachVideoToProdBlog, markProdBlogVideoFailed
} from '../services/accountAutomationService';
import {
  isAutomationAuthenticated, automationLogin, automationLogout,
  getAutomationUser, setAutomationAuthHandler, AUTOMATION_API_URL, IS_ALREADY_ON_PRODUCTION
} from '../services/automationApi';

// Public consumer site that renders published blogs (the affiliate-app at
// c.boosthealthdaily.com). Override per-env with REACT_APP_PUBLIC_DOMAIN.
const PUBLIC_BLOG_BASE = process.env.REACT_APP_PUBLIC_DOMAIN || 'https://c.boosthealthdaily.com';

// Mockup palette.
const PURPLE = '#5b4cdb';
const PURPLE_DARK = '#4a3cc4';
const RED = '#e23b3b';
const RED_DARK = '#c92f2f';

const PLATFORMS = [
  { value: 'youtube', label: 'YouTube', Icon: YouTubeIcon, enabled: true },
  { value: 'instagram', label: 'Instagram', Icon: InstagramIcon, enabled: true },
  { value: 'tiktok', label: 'TikTok', Icon: TikTokIcon, enabled: false }
];

// When a range's upper thumb sits at the ceiling, treat it as "no upper limit".
// This matters because results are sorted by view count (top creators first), so a
// real maximum cap would filter out exactly the creators we want to surface.
const VIEW_MIN = 0;
const VIEW_MAX = 1000000;   // ceiling = "1M+" (unlimited)
const COMMENT_MIN = 0;
const COMMENT_MAX = 10000;  // ceiling = "10k+" (unlimited)

const formatNumber = (n) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString();

// Compact abbreviations for slider labels (1000 -> "1k", 1000000 -> "1M").
// Instagram rows can carry null comments/likes/views (not available via Serper
// — see service.js) so this must not choke on null/undefined.
const abbreviate = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 ? 1 : 0)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 ? 1 : 0)}k`;
  return String(n);
};

// Upper-bound label: show "+" when at the ceiling to signal "unlimited".
const upperLabel = (value, max) => (value >= max ? `${abbreviate(max)}+` : abbreviate(value));

// TEST ONLY — remove before production. A dummy creator pinned to the top of every
// scan so the Add + invite-email flow can be exercised against a real inbox.
const TEST_CREATOR = {
  videoId: 'TEST_mzhang66',
  videoTitle: '[TEST] Sample health video — flow testing',
  videoUrl: 'https://www.youtube.com/',
  publishedAt: new Date().toISOString(),
  thumbnail: null,
  views: 999999,
  comments: 1234,
  likes: 5678,
  channelId: 'TEST_CHANNEL_mzhang66',
  channelTitle: '[TEST] mzhang66',
  channelUrl: 'https://www.youtube.com/',
  subscriberCount: 100000,
  contactEmails: ['mzhang66+auto1@gmail.com'],
  contactSocial: {}
};

const sectionLabelSx = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: 'text.secondary',
  textTransform: 'uppercase',
  mb: 1,
  display: 'block'
};

// Cache scan results (+ the offer/category they were attached to and any
// ADD/blog state) in sessionStorage, so navigating to another tab and back
// doesn't lose the last scan. Cleared on tab close, or via the Clear button.
const RESULTS_CACHE_KEY = 'accountAutomation.resultsCache';

const loadResultsCache = () => {
  try {
    const raw = sessionStorage.getItem(RESULTS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const saveResultsCache = (data) => {
  try {
    sessionStorage.setItem(RESULTS_CACHE_KEY, JSON.stringify(data));
  } catch (e) { /* storage unavailable/full — cache is best-effort */ }
};

const clearResultsCache = () => {
  try { sessionStorage.removeItem(RESULTS_CACHE_KEY); } catch (e) { /* noop */ }
};

const handleFrom = (title) =>
  '@' + (title || '').toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 18);

// Identity used for created/blog tracking. YouTube rows have a real channelId;
// Instagram rows (one per post, poster may be unresolved) fall back to the
// handle, then the row's own videoId so tracking never collides across rows.
const rowKey = (row) => row.channelId || row.handle || row.videoId;

// Size the MEDIA thumbnail box by the video's actual format: 9:16 for Shorts,
// 16:9 for regular videos. NOTE: the YouTube API always returns a letterboxed
// 16:9 thumbnail image even for Shorts (no portrait thumbnail is exposed), so
// we can't size from thumbnailWidth/Height — we key off `row.isShort` (duration
// <=3min) instead. The image itself still gets cover-cropped into whichever box.
const THUMB_MAX = 120;
const thumbnailBoxSize = (row) =>
  row?.isShort
    ? { width: Math.round(THUMB_MAX * 9 / 16), height: THUMB_MAX }
    : { width: THUMB_MAX, height: Math.round(THUMB_MAX * 9 / 16) };

const AccountAutomation = () => {
  const cached = loadResultsCache();
  // On the deployed production admin portal, this IS the main app's own
  // production session — used below instead of the dedicated-login flow.
  const { user: mainUser } = useAuth();

  const [platform, setPlatform] = useState('youtube');
  const [offers, setOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState(cached?.selectedOfferId || '');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(cached?.selectedCategoryId || '');
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(cached?.selectedSubcategoryId || '');
  const [keywordInput, setKeywordInput] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState(cached?.selectedKeywords || []);
  const [viewRange, setViewRange] = useState([1000, VIEW_MAX]);
  const [commentRange, setCommentRange] = useState([0, COMMENT_MAX]);
  const [shortsOnly, setShortsOnly] = useState(false); // false = combined (Shorts + regular videos)

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(cached?.results || []);
  const [searched, setSearched] = useState(cached?.searched || false);
  const [nextPageToken, setNextPageToken] = useState(cached?.nextPageToken || null);
  const [activeFilters, setActiveFilters] = useState(cached?.activeFilters || null);
  const [creatingId, setCreatingId] = useState(null);
  const [createdIds, setCreatedIds] = useState(cached?.createdIds || {});
  const [createdBlogs, setCreatedBlogs] = useState(cached?.createdBlogs || {});
  const [findingId, setFindingId] = useState(null);
  // Per-row background email-lookup status, keyed by rowKey(row):
  // 'searching' | 'done' | 'notfound' | 'error'. Drives the automatic
  // background Hunter/site-scrape pass below (and the manual retry button).
  const [emailLookupStatus, setEmailLookupStatus] = useState({});
  // Emails (lowercased) that were found specifically via Hunter.io's domain
  // search, as opposed to the site-scrape or the description-regex that runs
  // at search time — used to render the "(found by Hunter)" note.
  const [hunterEmails, setHunterEmails] = useState(new Set());
  const [sendInvite, setSendInvite] = useState(false);
  const [createBlog, setCreateBlog] = useState(true);
  // Self-hosting downloads via the split local-download flow needs an actual
  // local machine to run yt-dlp on — meaningless (and would just retry the
  // broken production-IP download) when this build IS production itself.
  const [downloadVideo, setDownloadVideo] = useState(!IS_ALREADY_ON_PRODUCTION);
  const [onlyWithEmail, setOnlyWithEmail] = useState(false);
  const [quota, setQuota] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Account Automation always operates against the production backend (see
  // services/automationApi.js) — separate from whatever backend the rest of
  // this client is otherwise pointed at, since dev/prod don't share a JWT
  // secret. Gate data loading + actions behind a dedicated production sign-in.
  const [prodAuthed, setProdAuthed] = useState(isAutomationAuthenticated());
  const [prodUser, setProdUser] = useState(getAutomationUser());
  const [authDialogOpen, setAuthDialogOpen] = useState(!isAutomationAuthenticated());
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const selectedOffer = offers.find((o) => o.id === selectedOfferId) || null;

  // Any automation call that comes back 401 (no session / expired session)
  // re-opens the sign-in dialog instead of silently failing.
  useEffect(() => {
    setAutomationAuthHandler(() => {
      setProdAuthed(false);
      setProdUser(null);
      setAuthDialogOpen(true);
    });
    return () => setAutomationAuthHandler(null);
  }, []);

  const handleProdSignIn = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const { user } = await automationLogin(authEmail, authPassword);
      setProdAuthed(true);
      setProdUser(user);
      setAuthDialogOpen(false);
      setAuthPassword('');
    } catch (err) {
      setAuthError(err.response?.data?.message || 'Sign-in failed. Check your production admin credentials.');
    } finally {
      setAuthLoading(false);
    }
  };

  // Not shown on the deployed production portal (IS_ALREADY_ON_PRODUCTION) —
  // there's nothing separate to disconnect from there; this tab is just
  // reusing the main app's own session, and "disconnecting" would really
  // mean signing out of the whole app, which isn't this button's job.
  const handleProdSignOut = () => {
    automationLogout();
    setProdAuthed(false);
    setProdUser(null);
    setAuthDialogOpen(true);
  };

  useEffect(() => {
    if (!prodAuthed) return;
    getQuota().then(setQuota).catch(() => {});
    getOffersForAutomation(1, 100)
      .then((data) => {
        const list = (data.offers || []).map((o) => ({
          id: o._id,
          title: o.title,
          brand: o.brand?.name || null,
          purchaseUrl: o.purchaseUrl || null,
          // Every offer belongs to exactly one category/subcategory — once an
          // offer is picked, its own category/subcategory replaces (and locks)
          // the manual selectors below.
          categoryId: o.category?._id || null,
          subcategoryId: o.subcategoryId || null
        }));
        setOffers(list);
      })
      .catch(() => {});
    getCategoriesForAutomation().then(setCategories).catch(() => {});
  }, [prodAuthed]);

  const selectedCategory = categories.find((c) => c._id === selectedCategoryId) || null;
  const selectedSubcategory = (selectedCategory?.subcategories || []).find((s) => s._id === selectedSubcategoryId) || null;

  // Poll any blog still waiting on its background YouTube-download-to-S3 job so
  // "Processing blog…" flips to "View blog" (or a failure note) once it's done.
  useEffect(() => {
    const pending = Object.entries(createdBlogs).filter(([, b]) => b.status === 'pending');
    if (!pending.length) return undefined;
    const interval = setInterval(() => {
      pending.forEach(([channelId, b]) => {
        getBlogVideoStatus(b.id)
          .then((data) => {
            if (data.status === 'pending') return;
            setCreatedBlogs((prev) => (prev[channelId]
              ? { ...prev, [channelId]: { ...prev[channelId], status: data.status, slug: data.slug || prev[channelId].slug, error: data.error || prev[channelId].error } }
              : prev));
          })
          .catch(() => {}); // transient network error — try again next tick
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [createdBlogs]);

  // Persist results (+ the offer/category/keywords they belong to) so switching
  // tabs and coming back doesn't lose the last scan. Cleared by handleClear.
  useEffect(() => {
    if (!searched) return;
    saveResultsCache({
      results, searched, nextPageToken, activeFilters, createdIds, createdBlogs,
      selectedKeywords, selectedOfferId, selectedCategoryId, selectedSubcategoryId
    });
  }, [results, searched, nextPageToken, activeFilters, createdIds, createdBlogs,
    selectedKeywords, selectedOfferId, selectedCategoryId, selectedSubcategoryId]);

  // Wipe cached results and reset the results view so the next scan starts clean.
  const handleClearResults = () => {
    setResults([]);
    setSearched(false);
    setNextPageToken(null);
    setActiveFilters(null);
    setCreatedIds({});
    setCreatedBlogs({});
    setError('');
    clearResultsCache();
  };

  const addKeyword = () => {
    const k = keywordInput.trim();
    if (k && !selectedKeywords.includes(k)) {
      setSelectedKeywords((prev) => [...prev, k]);
    }
    setKeywordInput('');
  };

  const removeKeyword = (k) =>
    setSelectedKeywords((prev) => prev.filter((x) => x !== k));

  const handleScan = async () => {
    if (!prodAuthed) { setAuthDialogOpen(true); return; }
    // Fold any text still sitting in the keyword box into the search, even if the
    // user didn't press Enter to turn it into a chip.
    const typed = keywordInput.trim();
    const activeKeywords = typed && !selectedKeywords.includes(typed)
      ? [...selectedKeywords, typed]
      : selectedKeywords;
    if (typed) {
      setSelectedKeywords(activeKeywords);
      setKeywordInput('');
    }

    setLoading(true);
    setError('');
    setSearched(true);

    const isIG = platform === 'instagram';
    // Build the search query: OR the keywords together, quoting multi-word
    // phrases. YouTube's API takes a regex-style OR ("a"|b); Google (Instagram,
    // via Serper) takes literal "OR" between terms.
    const query = activeKeywords.length
      ? activeKeywords.map((k) => (k.includes(' ') ? `"${k}"` : k)).join(isIG ? ' OR ' : '|')
      : (isIG ? 'fitness health' : 'health');

    // Instagram has no view/comment/follower metric available via Serper (see
    // service.js) — nothing here to filter by, so only keyword + maxResults
    // are sent. The view/comment range sliders are hidden for this platform.
    const filters = isIG
      ? {
        platform: 'instagram',
        keyword: query,
        maxResults: 100
      }
      : {
        keyword: query,
        minViews: viewRange[0],
        minComments: commentRange[0],
        // Omit upper caps when the thumb is at the ceiling (= unlimited), so the
        // top creators aren't filtered out.
        ...(viewRange[1] < VIEW_MAX ? { maxViews: viewRange[1] } : {}),
        ...(commentRange[1] < COMMENT_MAX ? { maxComments: commentRange[1] } : {}),
        order: 'viewCount',
        regionCode: 'US',
        maxResults: 100,
        ...(shortsOnly ? { shortsOnly: true } : {})
      };
    setActiveFilters(filters);
    try {
      const data = await searchCreators(filters);
      // TEST ONLY — pin the dummy creator to the top of YouTube scans so the
      // Add + invite-email flow can be exercised against a real inbox. Doesn't
      // apply to Instagram (its rows use a different shape). Remove TEST_CREATOR
      // usage for production.
      setResults(isIG ? (data.results || []) : [TEST_CREATOR, ...(data.results || [])]);
      setNextPageToken(data.nextPageToken || null);
      if (data.quota) setQuota(data.quota);
      autoFindEmails(data.results || []);
    } catch (err) {
      const msg = err.response?.data?.error || 'Scan failed. Please try again.';
      setError(msg);
      // TEST ONLY — still show the test creator (YouTube only) so the Add flow
      // works without a live search.
      setResults(isIG ? [] : [TEST_CREATOR]);
      setNextPageToken(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch the next batch of 100 using YouTube's page token, appending to the list.
  const handleLoadMore = async () => {
    if (!nextPageToken || !activeFilters || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await searchCreators({ ...activeFilters, pageToken: nextPageToken });
      setResults((prev) => [...prev, ...(data.results || [])]);
      setNextPageToken(data.nextPageToken || null);
      if (data.quota) setQuota(data.quota);
      autoFindEmails(data.results || []);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load more results.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleAdd = async (row) => {
    if (!prodAuthed) { setAuthDialogOpen(true); return; }
    const email = (row.contactEmails && row.contactEmails[0]) || '';
    const isIG = row.platform === 'instagram';
    setCreatingId(row.videoId);
    try {
      const account = await createPendingAccount({
        email,
        name: row.channelTitle,
        channelTitle: row.channelTitle,
        channelUrl: row.channelUrl,
        channelThumbnail: isIG ? null : row.channelThumbnail,
        social: row.contactSocial,
        offerId: selectedOffer ? selectedOffer.id : undefined,
        offerName: selectedOffer ? selectedOffer.title : undefined,
        offerBrand: selectedOffer ? selectedOffer.brand : undefined,
        offerPurchaseUrl: selectedOffer ? selectedOffer.purchaseUrl : undefined,
        categoryId: selectedCategoryId || undefined,
        subcategoryId: selectedSubcategoryId || undefined,
        sendInvite,
        // Blog auto-creation (embedding the discovered post/video) and
        // download-and-self-host both work for either platform now — the
        // download re-hosts the file via yt-dlp, whose Instagram extractor
        // covers public posts/reels the same way its YouTube extractor does.
        createBlog,
        downloadVideo: createBlog && downloadVideo,
        // Production's own outbound IP gets bot-checked/blocked by YouTube
        // (that's the recurring "S3 failed" issue) — so tell it to mark the
        // blog as awaiting a video WITHOUT running yt-dlp itself. This client
        // runs the actual download locally instead, then attaches the result
        // to this same blog on production. See the downloadVideoLocally call
        // below and accountAutomationService.js for the full explanation.
        deferDownload: createBlog && downloadVideo,
        keywords: selectedKeywords,
        video: createBlog ? {
          videoId: row.videoId,
          title: row.videoTitle,
          url: row.videoUrl,
          description: row.videoDescription || '',
          isShort: !!row.isShort
        } : undefined
      });
      setCreatedIds((prev) => ({ ...prev, [rowKey(row)]: true }));
      if (account.blog) {
        // videoDownloadStatus is 'pending' when a download was requested (either
        // queued on production directly, or deferred to the local flow below),
        // or undefined when no download was requested (blog is viewable right away).
        const status = account.blog.videoDownloadStatus || 'success';
        setCreatedBlogs((prev) => ({ ...prev, [rowKey(row)]: { ...account.blog, status } }));
      }

      // Deferred download: run yt-dlp against whichever backend THIS client is
      // normally pointed at (your local machine), then hand the resulting S3
      // URL to production to attach. Fire-and-forget — the existing polling
      // effect (watching for status !== 'pending') picks up the result same as
      // it always has, whether production or this local flow set it.
      if (account.videoDownload === 'deferred' && account.blog) {
        downloadVideoLocally({ videoId: row.videoId, url: row.videoUrl })
          .then(({ s3Url }) => attachVideoToProdBlog(account.blog.id, s3Url))
          .catch((downloadErr) => {
            const msg = downloadErr.response?.data?.error || downloadErr.message || 'Local video download failed.';
            console.error(`Local video download/attach failed for blog ${account.blog.id}:`, msg);
            return markProdBlogVideoFailed(account.blog.id, msg).catch(() => {});
          });
      }

      const parts = [`Pending account created for ${account.email}`];
      if (account.blog) parts.push('blog published');
      if (account.videoDownload === 'queued') parts.push('video downloading…');
      if (account.videoDownload === 'deferred') parts.push('video downloading locally…');
      if (sendInvite) parts.push(account.inviteSent ? 'invite emailed' : 'invite email failed');
      setSnackbar({
        open: true,
        message: parts.join(' · '),
        severity: sendInvite && !account.inviteSent ? 'warning' : 'success'
      });
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create pending account.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setCreatingId(null);
    }
  };

  const handleRemove = (row) =>
    setResults((prev) => prev.filter((r) => r.videoId !== row.videoId));

  // Shared by both the automatic background pass below and the manual retry
  // button: scrape the creator's website + Hunter.io for emails, merge any
  // found into that row, and track which ones came specifically from Hunter
  // (data.sources.hunter) so the table can note that next to the email.
  const lookupEmailForRow = async (row) => {
    const key = rowKey(row);
    setEmailLookupStatus((prev) => ({ ...prev, [key]: 'searching' }));
    try {
      const data = await findEmail({ website: row.website });
      const found = data.emails || [];
      const hunterFound = data.sources?.hunter || [];
      if (hunterFound.length) {
        setHunterEmails((prev) => new Set([...prev, ...hunterFound.map((e) => e.toLowerCase())]));
      }
      if (found.length) {
        setResults((prev) => prev.map((r) =>
          r.videoId === row.videoId
            ? { ...r, contactEmails: [...new Set([...(r.contactEmails || []), ...found])] }
            : r
        ));
      }
      setEmailLookupStatus((prev) => ({ ...prev, [key]: found.length ? 'done' : 'notfound' }));
      return { found, hunterConfigured: data.hunterConfigured, domain: data.domain };
    } catch (err) {
      setEmailLookupStatus((prev) => ({ ...prev, [key]: 'error' }));
      throw err;
    }
  };

  // Manual retry: the automatic pass below already runs this for every row as
  // soon as results come in, so this button only shows once that pass has
  // finished with nothing found (or errored) for a given row.
  const handleFindEmail = async (row) => {
    if (!prodAuthed) { setAuthDialogOpen(true); return; }
    if (!row.website) return;
    setFindingId(row.videoId);
    try {
      const { found, hunterConfigured, domain } = await lookupEmailForRow(row);
      if (found.length) {
        setSnackbar({ open: true, message: `Found ${found.length} email${found.length === 1 ? '' : 's'} for ${row.channelTitle}`, severity: 'success' });
      } else {
        const hint = hunterConfigured ? '' : ' (set HUNTER_API_KEY for deeper lookups)';
        setSnackbar({ open: true, message: `No email found on ${domain}${hint}`, severity: 'info' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Email lookup failed.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setFindingId(null);
    }
  };

  // Automatic background pass: as soon as a batch of results (a fresh scan or
  // a "load more" page) comes in, silently run lookupEmailForRow for every row
  // that has a website but no email yet — no click required. Concurrency is
  // capped so a 100-row page doesn't fire 100 Hunter.io/site-scrape calls at
  // once; failures are absorbed here (status flips to 'error' and the row
  // falls back to a manual "Retry" button) rather than surfaced as snackbars,
  // since this runs unattended in the background.
  const AUTO_EMAIL_LOOKUP_CONCURRENCY = 4;
  const autoFindEmails = (rows) => {
    if (!prodAuthed) return; // find-email is a production-only endpoint
    const targets = rows.filter((r) => r.website && !(r.contactEmails && r.contactEmails.length));
    if (!targets.length) return;

    let cursor = 0;
    const worker = async () => {
      while (cursor < targets.length) {
        const row = targets[cursor++];
        await lookupEmailForRow(row).catch(() => {});
      }
    };
    Array.from({ length: Math.min(AUTO_EMAIL_LOOKUP_CONCURRENCY, targets.length) }, worker);
  };

  const handleExport = () => {
    const header = ['Channel', 'Channel URL', 'Email', 'Keywords', 'Offer', 'Views', 'Comments', 'Likes', 'Video URL'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = visibleResults.map((r) => [
      r.channelTitle,
      r.channelUrl,
      (r.contactEmails || []).join('; '),
      selectedKeywords.join('; '),
      selectedOffer ? selectedOffer.title : '',
      r.views,
      r.comments,
      r.likes,
      r.videoUrl
    ].map(escape).join(','));
    const csv = [header.map(escape).join(','), ...rows].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'creator-discovery.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const visibleResults = onlyWithEmail
    ? results.filter((r) => r.contactEmails && r.contactEmails.length)
    : results;

  const cardSx = {
    p: 3,
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 'none'
  };

  const platformBtnSx = (p, active) => ({
    flex: 1,
    py: 1.5,
    borderRadius: 2,
    border: '1px solid',
    borderColor: active ? PURPLE : 'divider',
    bgcolor: active ? PURPLE : 'transparent',
    color: active ? '#fff' : (p.enabled ? 'text.primary' : 'text.disabled'),
    textTransform: 'none',
    fontWeight: 600,
    cursor: p.enabled ? 'pointer' : 'not-allowed',
    '&:hover': { bgcolor: active ? PURPLE_DARK : 'action.hover' }
  });

  return (
    <DashboardLayout title="Automation Dashboard">
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* ---- Production connection banner ----
            Account Automation always runs against the live production
            backend (real S3 uploads, real pending-affiliate accounts, real
            published blogs) — independent of whatever backend the rest of
            this app instance is pointed at. Make that unmistakable. */}
        <Alert
          severity={prodAuthed ? 'success' : 'warning'}
          icon={prodAuthed ? <CheckCircleIcon fontSize="inherit" /> : <LockIcon fontSize="inherit" />}
          sx={{ mb: 3 }}
          action={
            // On the deployed production portal there's nothing separate to
            // connect/disconnect — this tab just reuses the main app's own
            // (already production) session, so no action button at all.
            IS_ALREADY_ON_PRODUCTION ? null : prodAuthed ? (
              <Button color="inherit" size="small" onClick={handleProdSignOut} sx={{ textTransform: 'none' }}>
                Disconnect
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => setAuthDialogOpen(true)} sx={{ textTransform: 'none' }}>
                Connect
              </Button>
            )
          }
        >
          {IS_ALREADY_ON_PRODUCTION
            ? <>Running on <strong>production</strong>{mainUser?.email ? ` as ${mainUser.email}` : ''} — actions here create real accounts, blogs, and S3 uploads.</>
            : prodAuthed
              ? <>Connected to <strong>production</strong> ({AUTOMATION_API_URL}){prodUser?.email ? ` as ${prodUser.email}` : ''} — actions here create real accounts, blogs, and S3 uploads.</>
              : <>Not connected to production. Sign in with a production admin account to scan, create pending affiliates, or publish blogs.</>}
        </Alert>

        {/* ---- Control panel ---- */}
        <Paper sx={cardSx}>
          <Grid container spacing={4}>
            {/* Left column */}
            <Grid item xs={12} md={8}>
              <Typography sx={sectionLabelSx}>Select Platform</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {PLATFORMS.map((p) => {
                  const active = platform === p.value;
                  return (
                    <Tooltip key={p.value} title={p.enabled ? '' : 'Coming soon'}>
                      <Box
                        onClick={() => {
                          if (!p.enabled || p.value === platform) return;
                          setPlatform(p.value);
                          // Previous platform's results/filters don't apply here — avoid
                          // showing e.g. YouTube rows while the Instagram tab is active.
                          handleClearResults();
                        }}
                        sx={{
                          ...platformBtnSx(p, active),
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                          userSelect: 'none'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <p.Icon fontSize="small" />
                          <span>{p.label}</span>
                        </Box>
                        {!p.enabled && (
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.8 }}>
                            Coming soon
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>

              <Typography sx={sectionLabelSx}>Offer</Typography>
              <TextField
                fullWidth size="small" select
                value={selectedOfferId}
                onChange={(e) => {
                  const newOfferId = e.target.value;
                  setSelectedOfferId(newOfferId);
                  // Every offer belongs to exactly one category/subcategory —
                  // once one's picked, show (and lock) its own, rather than
                  // whatever was manually selected before.
                  const offer = offers.find((o) => o.id === newOfferId);
                  if (offer) {
                    setSelectedCategoryId(offer.categoryId || '');
                    setSelectedSubcategoryId(offer.subcategoryId || '');
                  }
                }}
                sx={{ mb: 3 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value=""><em>Select an offer…</em></MenuItem>
                {offers.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.title}</MenuItem>
                ))}
              </TextField>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography sx={sectionLabelSx}>Category</Typography>
                  <TextField
                    fullWidth size="small" select
                    value={selectedCategoryId}
                    onChange={(e) => {
                      setSelectedCategoryId(e.target.value);
                      setSelectedSubcategoryId(''); // subcategory list depends on category
                    }}
                    disabled={!!selectedOfferId}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {categories.map((c) => (
                      <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={sectionLabelSx}>Subcategory</Typography>
                  <TextField
                    fullWidth size="small" select
                    value={selectedSubcategoryId}
                    onChange={(e) => setSelectedSubcategoryId(e.target.value)}
                    disabled={!selectedCategory || !!selectedOfferId}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {(selectedCategory?.subcategories || []).map((s) => (
                      <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {selectedOfferId
                      ? "Set automatically from the selected offer's own category/subcategory."
                      : 'Optional — lets the auto-created blog join that subcategory\'s "snake" (merged blog), same as manually created blogs.'}
                  </Typography>
                </Grid>
              </Grid>

              {platform === 'instagram' ? (
                // No view/comment/follower metric exists for Instagram via Serper
                // (confirmed against live responses — see service.js) — showing a
                // slider that can't actually filter anything would be misleading,
                // so it's replaced with a plain note instead.
                <Alert severity="info" sx={{ mb: 3 }}>
                  Instagram results can't be filtered or ranked by reach — Google's search index doesn't expose follower, view, or comment counts. Results are Google's own relevance order for your keyword.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography sx={sectionLabelSx}>View Count Range</Typography>
                      <Typography variant="caption" sx={{ color: PURPLE, fontWeight: 700 }}>
                        {abbreviate(viewRange[0])} - {upperLabel(viewRange[1], VIEW_MAX)}
                      </Typography>
                    </Box>
                    <Slider
                      value={viewRange}
                      onChange={(e, v) => setViewRange(v)}
                      min={VIEW_MIN} max={VIEW_MAX} step={1000}
                      valueLabelDisplay="auto" valueLabelFormat={abbreviate}
                      sx={{ color: PURPLE }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography sx={sectionLabelSx}>Comment Range</Typography>
                      <Typography variant="caption" sx={{ color: PURPLE, fontWeight: 700 }}>
                        {abbreviate(commentRange[0])} - {upperLabel(commentRange[1], COMMENT_MAX)}
                      </Typography>
                    </Box>
                    <Slider
                      value={commentRange}
                      onChange={(e, v) => setCommentRange(v)}
                      min={COMMENT_MIN} max={COMMENT_MAX} step={50}
                      valueLabelDisplay="auto" valueLabelFormat={abbreviate}
                      sx={{ color: PURPLE }}
                    />
                  </Grid>
                </Grid>
              )}

              {platform !== 'instagram' && (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={<Switch checked={shortsOnly} onChange={(e) => setShortsOnly(e.target.checked)} />}
                  label={
                    <Typography variant="body2">
                      Shorts only{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        (off = Shorts + regular videos)
                      </Typography>
                    </Typography>
                  }
                />
              )}

              <Button
                fullWidth onClick={handleScan} disabled={loading}
                startIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <RocketIcon />}
                sx={{
                  mt: 3, py: 1.75, borderRadius: 2, bgcolor: PURPLE, color: '#fff',
                  fontSize: '1rem', fontWeight: 700, textTransform: 'none',
                  '&:hover': { bgcolor: PURPLE_DARK }
                }}
              >
                {loading ? 'Scanning…' : 'Start Automation Scan'}
              </Button>
            </Grid>

            {/* Right column */}
            <Grid item xs={12} md={4}>
              <Typography sx={sectionLabelSx}>Search Keyword</Typography>
              <TextField
                fullWidth size="small"
                placeholder={platform === 'instagram' ? 'e.g. fitness transformation' : 'e.g. tech reviews 2024'}
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />

              <Typography sx={sectionLabelSx}>Selected Keywords</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {selectedKeywords.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Type a keyword and press Enter
                  </Typography>
                )}
                {selectedKeywords.map((k) => (
                  <Chip
                    key={k} label={k} onDelete={() => removeKeyword(k)}
                    sx={{ bgcolor: '#3aa0ec', color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)' } }}
                  />
                ))}
              </Box>

              <FormControlLabel
                control={<Switch checked={createBlog} onChange={(e) => setCreateBlog(e.target.checked)} />}
                label={<Typography variant="body2">{platform === 'instagram' ? 'Create linked blog from post' : 'Create linked blog from video'}</Typography>}
              />
              <Tooltip title={IS_ALREADY_ON_PRODUCTION
                ? 'Not available from the deployed portal — self-hosting needs an actual local machine to run the download on (production\'s own IP gets bot-checked by YouTube). Run the dashboard locally to use this.'
                : 'Runs the download on this machine (not production — production\'s outbound IP gets bot-checked by YouTube) and uploads straight to the shared S3 bucket production also reads from.'}
              >
                <span>
                  <FormControlLabel
                    control={<Switch checked={downloadVideo} disabled={!createBlog || IS_ALREADY_ON_PRODUCTION} onChange={(e) => setDownloadVideo(e.target.checked)} />}
                    label={<Typography variant="body2">Download &amp; self-host the video (S3, via local machine)</Typography>}
                  />
                </span>
              </Tooltip>
              <FormControlLabel
                control={<Switch checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} />}
                label={<Typography variant="body2">Send invite email when adding</Typography>}
              />
              {quota && platform === 'youtube' && (
                <Box sx={{ mt: 1 }}>
                  <Tooltip title="Estimated by summing each call's documented cost (search = 100, videos/channels = 1 each). Resets midnight Pacific. Authoritative figure is in Google Cloud Console.">
                    <Chip
                      size="small"
                      color={quota.remaining <= 0 ? 'error' : quota.remaining < quota.dailyLimit * 0.1 ? 'warning' : 'success'}
                      label={`Quota left today: ~${formatNumber(quota.remaining)} / ${formatNumber(quota.dailyLimit)}`}
                    />
                  </Tooltip>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {/* ---- Results ---- */}
        {(loading || searched) && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>Video Discovery Results</Typography>
                <Chip
                  label={`${visibleResults.length} Videos Found`}
                  sx={{ bgcolor: '#bfe3ff', color: '#1a73c7', fontWeight: 700 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined" startIcon={<FilterIcon />}
                  onClick={() => setOnlyWithEmail((v) => !v)}
                  sx={{
                    textTransform: 'none', borderRadius: 2, color: onlyWithEmail ? PURPLE : 'text.primary',
                    borderColor: onlyWithEmail ? PURPLE : 'divider'
                  }}
                >
                  {onlyWithEmail ? 'With email' : 'Filter'}
                </Button>
                <Button
                  variant="contained" startIcon={<ExportIcon />} onClick={handleExport}
                  disabled={!visibleResults.length}
                  sx={{ textTransform: 'none', borderRadius: 2, bgcolor: PURPLE, '&:hover': { bgcolor: PURPLE_DARK } }}
                >
                  Export
                </Button>
                <Button
                  variant="outlined" startIcon={<ClearIcon />} onClick={handleClearResults}
                  sx={{ textTransform: 'none', borderRadius: 2, color: RED, borderColor: RED, '&:hover': { borderColor: RED_DARK, bgcolor: 'rgba(226,59,59,0.04)' } }}
                >
                  Clear
                </Button>
              </Box>
            </Box>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: PURPLE }} />
              </Box>
            )}

            {!loading && searched && !error && visibleResults.length === 0 && (
              <Alert severity="warning">No creators matched your filters. Try widening the view/comment ranges.</Alert>
            )}

            {!loading && visibleResults.length > 0 && (
              <Paper sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 980 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.05em' } }}>
                        <TableCell>USERNAME</TableCell>
                        <TableCell>MEDIA</TableCell>
                        <TableCell>EMAIL</TableCell>
                        <TableCell>KEYWORDS</TableCell>
                        <TableCell>OFFERS</TableCell>
                        <TableCell align="right">
                          {platform === 'instagram' ? (
                            <Tooltip title="Not available — Instagram doesn't expose view counts to Google's index">
                              <span>VIEWS</span>
                            </Tooltip>
                          ) : 'VIEWS'}
                        </TableCell>
                        <TableCell align="right">
                          {platform === 'instagram' ? (
                            <Tooltip title="Not available — Instagram doesn't expose comment counts to Google's index">
                              <span>COMMENTS</span>
                            </Tooltip>
                          ) : 'COMMENTS'}
                        </TableCell>
                        <TableCell align="right">
                          {platform === 'instagram' ? (
                            <Tooltip title="Not available — Instagram doesn't expose like counts to Google's index">
                              <span>LIKES</span>
                            </Tooltip>
                          ) : 'LIKES'}
                        </TableCell>
                        <TableCell align="center">ACTION</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleResults.map((row) => {
                        const email = (row.contactEmails && row.contactEmails[0]) || '';
                        const created = createdIds[rowKey(row)];
                        const isIGRow = row.platform === 'instagram';
                        return (
                          <TableRow key={row.videoId} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ bgcolor: PURPLE, width: 38, height: 38 }}>
                                  {row.channelTitle?.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  {row.channelUrl ? (
                                    <Link href={row.channelUrl} target="_blank" rel="noopener" underline="hover" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                      {row.channelTitle}
                                    </Link>
                                  ) : (
                                    <Typography sx={{ fontWeight: 700 }}>{row.channelTitle}</Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {isIGRow ? (row.handle || 'handle unresolved') : handleFrom(row.channelTitle)}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: thumbnailBoxSize(row).width }}>
                                <Link href={row.videoUrl} target="_blank" rel="noopener" sx={{ display: 'inline-block', lineHeight: 0 }}>
                                  <Avatar
                                    variant="rounded"
                                    src={row.thumbnail || undefined}
                                    sx={{ ...thumbnailBoxSize(row), bgcolor: 'action.hover', cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                                  >
                                    {isIGRow ? <InstagramIcon fontSize="small" sx={{ color: 'text.disabled' }} /> : <YouTubeIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                                  </Avatar>
                                </Link>
                                <Tooltip title={row.videoTitle || ''}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {row.videoTitle || '—'}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {email ? (
                                <Tooltip title={hunterEmails.has(email.toLowerCase()) ? `${email} — found by Hunter.io` : email}>
                                  <Typography variant="body2" sx={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {email}
                                    {hunterEmails.has(email.toLowerCase()) && (
                                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                        (found by Hunter)
                                      </Typography>
                                    )}
                                  </Typography>
                                </Tooltip>
                              ) : row.website ? (
                                emailLookupStatus[rowKey(row)] === 'searching' ? (
                                  <Tooltip title={`Searching ${row.website} + Hunter.io for an email…`}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                      <CircularProgress size={14} />
                                      <Typography variant="caption">Searching…</Typography>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={`Search ${row.website} + Hunter.io for an email`}>
                                    <Button
                                      size="small" variant="text"
                                      startIcon={findingId === row.videoId ? <CircularProgress size={14} /> : <SearchIcon fontSize="small" />}
                                      disabled={findingId === row.videoId}
                                      onClick={() => handleFindEmail(row)}
                                      sx={{ textTransform: 'none', color: PURPLE, p: 0, minWidth: 0 }}
                                    >
                                      {findingId === row.videoId
                                        ? 'Searching…'
                                        : (emailLookupStatus[rowKey(row)] === 'notfound' || emailLookupStatus[rowKey(row)] === 'error')
                                          ? 'Retry'
                                          : 'Find email'}
                                    </Button>
                                  </Tooltip>
                                )
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {(selectedKeywords.length ? selectedKeywords : ['—']).slice(0, 3).map((k) => (
                                  <Chip key={k} label={k} size="small" sx={{ bgcolor: '#e3f1ff', color: '#2563a8', width: 'fit-content' }} />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: PURPLE, fontWeight: 600, maxWidth: 120 }}>
                                {selectedOffer ? selectedOffer.title : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{abbreviate(row.views)}</TableCell>
                            <TableCell align="right">{abbreviate(row.comments)}</TableCell>
                            <TableCell align="right">{abbreviate(row.likes)}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'stretch', minWidth: 90 }}>
                                <Tooltip title={email ? 'Create a pending affiliate account' : 'No email found — a placeholder address will be used instead (invite email will not be sent)'}>
                                  <span>
                                    <Button
                                      fullWidth size="small" variant="contained"
                                      disabled={creatingId === row.videoId || created}
                                      onClick={() => handleAdd(row)}
                                      sx={{ bgcolor: PURPLE, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: PURPLE_DARK } }}
                                    >
                                      {created ? 'ADDED ✓' : (creatingId === row.videoId ? '…' : 'ADD')}
                                    </Button>
                                  </span>
                                </Tooltip>
                                <Button
                                  fullWidth size="small" variant="contained"
                                  onClick={() => handleRemove(row)}
                                  sx={{ bgcolor: RED, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: RED_DARK } }}
                                >
                                  REMOVE
                                </Button>
                                {createdBlogs[rowKey(row)] && (
                                  createdBlogs[rowKey(row)].status === 'pending' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                      <CircularProgress size={10} />
                                      <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                        Processing blog…
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Tooltip title={createdBlogs[rowKey(row)].status === 'failed'
                                      ? `Video download to S3 failed — blog is live with the YouTube embed instead.${createdBlogs[rowKey(row)].error ? ` (${createdBlogs[rowKey(row)].error})` : ''}`
                                      : ''}
                                    >
                                      <Link
                                        href={`${PUBLIC_BLOG_BASE}/blog/${createdBlogs[rowKey(row)].slug}`}
                                        target="_blank" rel="noopener"
                                        sx={{
                                          fontSize: '0.72rem', textAlign: 'center', fontWeight: 600,
                                          color: createdBlogs[rowKey(row)].status === 'failed' ? '#b45309' : PURPLE
                                        }}
                                      >
                                        {createdBlogs[rowKey(row)].status === 'failed' ? 'View blog ⚠' : 'View blog ↗'}
                                      </Link>
                                    </Tooltip>
                                  )
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}

            {!loading && nextPageToken && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  variant="outlined"
                  startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 4, color: PURPLE, borderColor: PURPLE }}
                >
                  {loadingMore ? 'Loading…' : 'Load 100 more results'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Production sign-in — required before any scan/create action fires,
            since dev and production don't share a JWT secret. */}
        <Dialog open={authDialogOpen && !IS_ALREADY_ON_PRODUCTION} onClose={() => prodAuthed && setAuthDialogOpen(false)} maxWidth="xs" fullWidth>
          <form onSubmit={handleProdSignIn}>
            <DialogTitle>Connect to production</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Account Automation always runs against the live production backend ({AUTOMATION_API_URL}),
                regardless of which environment this dashboard is otherwise pointed at. Sign in with a
                production admin account to continue.
              </Typography>
              {authError && <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>}
              <TextField
                autoFocus
                fullWidth
                label="Production admin email"
                type="email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                required
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              {prodAuthed && (
                <Button onClick={() => setAuthDialogOpen(false)} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={authLoading || !authEmail || !authPassword}
                startIcon={authLoading ? <CircularProgress size={16} /> : null}
                sx={{ textTransform: 'none', bgcolor: PURPLE, '&:hover': { bgcolor: PURPLE_DARK } }}
              >
                {authLoading ? 'Connecting…' : 'Connect'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default AccountAutomation;
